import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { formatDateOnly } from '../utils/formatDate';

const CATEGORIES = [
    { key: "Overview", label: "Overview", icon: "las la-eye" },
    { key: "Analytics & Tracking", label: "Analytics", icon: "las la-chart-bar" },
    { key: "JavaScript Frameworks", label: "JS Frameworks", icon: "las la-atom" },
    { key: "CSS Frameworks", label: "CSS Frameworks", icon: "las la-border-all" },
    { key: "CMS / Platforms", label: "CMS", icon: "las la-blog" },
    { key: "E-commerce", label: "E-commerce", icon: "las la-shopping-cart" },
    { key: "CDN / Hosting", label: "CDN", icon: "las la-cloud" },
    { key: "Web Servers", label: "Servers", icon: "las la-server" },
    { key: "Programming Languages", label: "Languages", icon: "las la-code" },
    { key: "Fonts", label: "Fonts", icon: "las la-font" },
    { key: "Video", label: "Video", icon: "las la-video" },
    { key: "Chat / Support", label: "Chat", icon: "las la-comments" },
    { key: "Security", label: "Security", icon: "las la-shield-alt" },
    { key: "Social / Sharing", label: "Social", icon: "las la-share-alt" },
    { key: "Email Marketing", label: "Email", icon: "las la-envelope" }
];

const getScreenshotUrl = (screenshotPath) => {
    if (!screenshotPath) return '';
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return `http://localhost:5100${screenshotPath}`;
    }
    return screenshotPath;
};

const BuildDetails = () => {
    const { domain } = useParams();
    const navigate = useNavigate();

    // Scan State Management
    const [scanData, setScanData] = useState(null);
    const [similarSites, setSimilarSites] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [scanProgress, setScanProgress] = useState(0);
    const [scanStatusMsg, setScanStatusMsg] = useState("Initializing Deep Technical Scan...");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Active Category Filter
    const [activeTab, setActiveTab] = useState("Overview");

    useEffect(() => {
        const startOrFetchScan = async () => {
            setLoading(true);
            setError(null);
            try {
                // Step 1: Check if already analyzed
                const res = await api.get(`/api/builtwith/results/${domain}`).catch(() => null);
                if (res && res.data && res.data.site) {
                    setScanData(res.data);
                    fetchSimilar(domain);
                    setLoading(false);
                    return;
                }

                // Step 2: Trigger automated stack analyzer scan
                setScanStatusMsg("Scanning system files, DNS, headers & browser binds...");
                const startRes = await api.post('/api/builtwith/analyze', { url: domain });
                if (startRes.data && startRes.data.jobId) {
                    setJobId(startRes.data.jobId);
                    setScanProgress(20);
                } else {
                    setError("Failed to initialize scan engine.");
                    setLoading(false);
                }
            } catch (err) {
                console.error("Scan init failed:", err);
                setError(err.response?.data?.error || "Connection timed out.");
                setLoading(false);
            }
        };

        if (domain) {
            startOrFetchScan();
        }
    }, [domain]);

    // Job Polling Loop
    useEffect(() => {
        if (!jobId) return;

        let interval = setInterval(async () => {
            try {
                const res = await api.get(`/api/builtwith/status/${jobId}`);
                const job = res.data;

                if (job.status === 'processing') {
                    setScanProgress(job.progress);
                    setScanStatusMsg(`Rendering website DOM & processing Puppeteer mocks (${job.progress}%)`);
                } else if (job.status === 'completed') {
                    clearInterval(interval);
                    setScanProgress(100);
                    // Fetch completed result
                    const resultRes = await api.get(`/api/builtwith/results/${domain}`);
                    setScanData(resultRes.data);
                    fetchSimilar(domain);
                    setLoading(false);
                    setJobId(null);
                } else if (job.status === 'failed') {
                    clearInterval(interval);
                    setError(job.error || "Headless scan failed to complete.");
                    setLoading(false);
                    setJobId(null);
                }
            } catch (err) {
                console.error("Job status polling failed:", err);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [jobId, domain]);

    const fetchSimilar = async (targetDomain) => {
        try {
            const res = await api.get(`/api/builtwith/similar/${targetDomain}`);
            if (res.data && res.data.similar) {
                setSimilarSites(res.data.similar);
            }
        } catch (e) {
            console.error("Failed to fetch similar stacks:", e);
        }
    };

    const triggerRescan = async () => {
        setLoading(true);
        setError(null);
        setScanStatusMsg("Re-initializing technology profile diagnostic scan...");
        try {
            const startRes = await api.post('/api/builtwith/analyze', { url: domain });
            if (startRes.data && startRes.data.jobId) {
                setJobId(startRes.data.jobId);
                setScanProgress(20);
            } else {
                setError("Failed to start scan.");
                setLoading(false);
            }
        } catch (err) {
            setError(err.response?.data?.error || "Failed to start scan.");
            setLoading(false);
        }
    };

    const getConfidenceBadge = (confidence) => {
        switch (confidence) {
            case 'confirmed': return <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1"><i className="las la-check-circle me-1"></i>Confirmed</span>;
            case 'likely': return <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2 py-1"><i className="las la-question-circle me-1"></i>Likely</span>;
            default: return <span className="badge bg-secondary-subtle text-secondary border px-2 py-1">Possible</span>;
        }
    };

    const getCategoryColor = (cat) => {
        switch (cat) {
            case 'Analytics & Tracking': return 'bg-info-subtle text-info border-info-subtle';
            case 'CMS / Platforms': return 'bg-success-subtle text-success border-success-subtle';
            case 'JavaScript Frameworks': return 'bg-primary-subtle text-primary border-primary-subtle';
            case 'Security': return 'bg-danger-subtle text-danger border-danger-subtle';
            case 'Web Servers': return 'bg-warning-subtle text-warning border-warning-subtle';
            default: return 'bg-light text-dark border';
        }
    };

    // Filter detections
    const getFilteredTechs = () => {
        if (!scanData || !scanData.technologies) return [];
        if (activeTab === 'Overview') return scanData.technologies;
        return scanData.technologies.filter(t => t.category === activeTab);
    };

    // Calculate dynamic stats
    const getDetectionsCount = (catName) => {
        if (!scanData || !scanData.technologies) return 0;
        return scanData.technologies.filter(t => t.category === catName).length;
    };

    return (
        <div className="container min-vh-100 py-4 bg-light">
            {/* Header section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-body py-3 d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 fw-bold">
                                Technology Profile for: <span className="text-primary">{domain}</span>
                            </h4>
                            <div className="d-flex gap-2">
                                <button className="btn btn-outline-primary btn-sm" onClick={triggerRescan}>
                                    <i className="las la-sync me-1"></i>Refresh Scan
                                </button>
                                <button className="btn btn-outline-dark btn-sm" onClick={() => navigate('/scans')}>
                                    ← Back to My Scans
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading / Polling scan screen */}
            {loading && !error && (
                <div className="row justify-content-center py-5">
                    <div className="col-12 col-md-8 col-lg-6 text-center">
                        <div className="card border-0 shadow-sm p-5 rounded-4">
                            <div className="spinner-grow text-primary mb-4" style={{ width: '3.5rem', height: '3.5rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h4 className="fw-bold mb-3">Running Stack Diagnostic scan</h4>
                            <p className="text-muted mb-4 small">{scanStatusMsg}</p>
                            
                            {/* Progress bar */}
                            <div className="progress mb-2" style={{ height: '8px' }}>
                                <div 
                                    className="progress-bar progress-bar-striped progress-bar-animated bg-success" 
                                    role="progressbar" 
                                    style={{ width: `${scanProgress}%` }}
                                ></div>
                            </div>
                            <span className="text-success fw-bold small">{scanProgress}% completed</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Error screen */}
            {error && (
                <div className="row justify-content-center py-5">
                    <div className="col-12 col-md-8 col-lg-6 text-center">
                        <div className="card border-0 shadow-sm p-5 rounded-4">
                            <i className="las la-exclamation-triangle text-danger display-1 mb-4"></i>
                            <h4 className="text-danger fw-bold mb-2">Scan Failed</h4>
                            <p className="text-muted small mb-4">{error}</p>
                            <div>
                                <button onClick={() => window.location.reload()} className="btn btn-primary rounded-pill px-4 me-2">
                                    <i className="las la-sync me-2"></i>Retry Scan
                                </button>
                                <button onClick={() => navigate('/scans')} className="btn btn-outline-secondary rounded-pill px-4">
                                    Back to Scans
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            {!loading && !error && scanData && (
                <div className="row g-4">
                    {/* Left main column: Screenshot + Tabs + Tech Cards */}
                    <div className="col-12 col-lg-8">
                        {/* Live Site Screenshot mock browser frame */}
                        {scanData.site.screenshot_path && (
                            <div className="card border shadow-sm rounded-4 overflow-hidden mb-4 bg-white">
                                <div className="bg-light border-bottom py-2 px-3 d-flex align-items-center gap-2">
                                    <span className="bg-danger rounded-circle d-inline-block" style={{ width: '10px', height: '10px' }}></span>
                                    <span className="bg-warning rounded-circle d-inline-block" style={{ width: '10px', height: '10px' }}></span>
                                    <span className="bg-success rounded-circle d-inline-block" style={{ width: '10px', height: '10px' }}></span>
                                    <div className="bg-white border rounded px-3 py-1 text-muted text-center flex-grow-1 mx-5 small text-truncate">
                                        <i className="las la-lock text-success me-2"></i>{scanData.site.url}
                                    </div>
                                </div>
                                <div className="text-center p-0">
                                    <img 
                                        src={getScreenshotUrl(scanData.site.screenshot_path)} 
                                        className="img-fluid w-100 object-fit-cover" 
                                        alt={`${domain} Live Capture`} 
                                        style={{ maxHeight: '380px' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Category filtering navigation tabs */}
                        <div className="card border shadow-sm rounded-4 mb-4 bg-white">
                            <div className="card-body p-3">
                                <div className="row row-cols-2 row-cols-sm-3 row-cols-md-5 g-2">
                                    {CATEGORIES.map((tab) => {
                                        const count = getDetectionsCount(tab.key);
                                        const isActive = activeTab === tab.key;
                                        return (
                                            <div key={tab.key} className="col">
                                                <button 
                                                    onClick={() => setActiveTab(tab.key)}
                                                    className={`btn btn-sm w-100 h-100 d-flex align-items-center justify-content-between px-2.5 py-2.5 transition-all rounded-3 border ${
                                                        isActive 
                                                            ? 'btn-primary border-primary shadow-sm text-white' 
                                                            : 'btn-light border-light-subtle text-dark bg-white'
                                                    }`}
                                                    style={{ minHeight: '44px' }}
                                                >
                                                    <div className="d-flex align-items-center text-truncate me-1">
                                                        <i className={`${tab.icon} me-1.5 fs-5 ${isActive ? 'text-white' : 'text-primary'}`}></i>
                                                        <span className="text-truncate small fw-semibold" style={{ fontSize: '0.82rem' }}>{tab.label}</span>
                                                    </div>
                                                    {tab.key !== 'Overview' && (
                                                        <span className={`badge rounded-pill small ${
                                                            isActive ? 'bg-white text-primary' : 'bg-secondary-subtle text-secondary border'
                                                        }`} style={{ minWidth: '18px', padding: '0.25em 0.4em', fontSize: '0.72rem' }}>
                                                            {count}
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Technologies List */}
                        <div className="card border shadow-sm rounded-4 bg-white">
                            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                                <h5 className="mb-0 fw-bold text-dark">{activeTab} Details</h5>
                                <span className="badge bg-light text-muted border px-3 py-1">
                                    Showing {getFilteredTechs().length} Components
                                </span>
                            </div>
                            <div className="card-body p-0">
                                {getFilteredTechs().length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="las la-cubes display-3 mb-2 text-secondary"></i>
                                        <p className="mb-0">No technologies detected matching this category filter.</p>
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {getFilteredTechs().map((item, i) => (
                                            <div key={i} className="list-group-item p-4 border-bottom border-light">
                                                <div className="d-flex align-items-start justify-content-between mb-2">
                                                    <div className="d-flex align-items-center">
                                                        <img 
                                                            src={`https://cdn.simpleicons.org/${item.icon_slug || 'code'}`} 
                                                            className="me-3" 
                                                            alt="" 
                                                            style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                                            onError={(e) => { e.target.src = 'https://cdn.simpleicons.org/code' }}
                                                        />
                                                        <div>
                                                            <h5 className="mb-0 fw-bold text-dark">{item.name}</h5>
                                                            <span className={`badge border ${getCategoryColor(item.category)} rounded-pill px-2 py-0.5 mt-1 small`}>
                                                                {item.category}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {getConfidenceBadge(item.confidence)}
                                                    </div>
                                                </div>
                                                
                                                <p className="text-muted small leading-relaxed my-3">{item.description}</p>
                                                
                                                <div className="row g-2 border-top pt-3 mt-2 bg-light-subtle rounded p-2">
                                                    <div className="col-12 col-md-6">
                                                        <small className="text-muted d-block">
                                                            <i className="las la-calendar me-1"></i>
                                                            First Detected: {formatDateOnly(item.first_detected_at || Date.now())}
                                                        </small>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <small className="text-muted d-block">
                                                            <i className="las la-history me-1"></i>
                                                            Last Detected: {formatDateOnly(item.last_detected_at || Date.now())}
                                                        </small>
                                                    </div>
                                                    {item.detection_method && (
                                                        <div className="col-12 mt-1">
                                                            <small className="text-muted d-block">
                                                                <i className="las la-binoculars me-1 text-primary"></i>
                                                                Method: <code className="text-dark small">{item.detection_method} ({item.detection_detail})</code>
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>

                                                {item.website_url && (
                                                    <div className="mt-3 text-end">
                                                        <a 
                                                            href={item.website_url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="btn btn-link btn-sm text-primary p-0 text-decoration-none fw-bold"
                                                        >
                                                            Visit official site →
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right column: WHOIS sidebar, Similar sites, category count pill badges */}
                    <div className="col-12 col-lg-4">
                        {/* Domain age and registrar */}
                        <div className="card border shadow-sm rounded-4 mb-4 bg-white">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-dark text-uppercase tracking-wider">
                                    <i className="las la-globe me-2 text-primary"></i>Profile Summary
                                </h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3 border-bottom pb-2">
                                    <span className="text-muted small d-block">Domain Registrar</span>
                                    <span className="fw-bold text-dark">{scanData.site.whois_registrar || "Unknown"}</span>
                                </div>
                                <div className="mb-3 border-bottom pb-2">
                                    <span className="text-muted small d-block">Registered On</span>
                                    <span className="fw-bold text-dark">
                                        {scanData.site.whois_created_at ? formatDateOnly(scanData.site.whois_created_at) : "Unknown"}
                                    </span>
                                </div>
                                <div className="mb-3 border-bottom pb-2">
                                    <span className="text-muted small d-block">Registrant Country</span>
                                    <span className="fw-bold text-dark">
                                        <i className="las la-map-marker me-1 text-danger"></i>
                                        {scanData.site.whois_country || "Unknown"}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted small d-block">SSL Issuer Certificate</span>
                                    <span className="fw-bold text-dark text-truncate d-block small">
                                        <i className="las la-certificate text-success me-1"></i>
                                        {scanData.site.ssl_issuer || "None"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Similar websites */}
                        <div className="card border shadow-sm rounded-4 mb-4 bg-white">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-dark text-uppercase tracking-wider">
                                    <i className="las la-link me-2 text-success"></i>Similar Stack Sites
                                </h6>
                            </div>
                            <div className="card-body p-0">
                                {similarSites.length === 0 ? (
                                    <div className="text-center py-4 text-muted small">
                                        No similar sites indexed yet.
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {similarSites.map((site, i) => (
                                            <div 
                                                key={i} 
                                                className="list-group-item d-flex align-items-center justify-content-between p-3 cursor-pointer hover:bg-light"
                                                onClick={() => navigate(`/build-details/${site.domain}`)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center text-truncate">
                                                    <i className="las la-external-link-alt text-muted me-2"></i>
                                                    <span className="fw-bold text-dark small text-truncate">{site.domain}</span>
                                                </div>
                                                <span className="badge bg-success-subtle text-success rounded-pill small">
                                                    {site.matching_count} shared
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Category Detections Counts */}
                        <div className="card border shadow-sm rounded-4 bg-white">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-dark text-uppercase tracking-wider">
                                    <i className="las la-chart-pie me-2 text-warning"></i>Tech Stack Summary
                                </h6>
                            </div>
                            <div className="card-body">
                                {CATEGORIES.filter(c => c.key !== 'Overview').map((c, i) => {
                                    const count = getDetectionsCount(c.key);
                                    if (count === 0) return null;
                                    return (
                                        <div key={i} className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                                            <span className="text-muted small">{c.label}</span>
                                            <span className="badge bg-secondary-subtle text-secondary rounded-pill px-3 py-1">
                                                {count} detected
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuildDetails;
